/**
 * Format a monetary amount with the correct currency symbol and locale.
 *
 * Examples:
 *   formatCurrency(500000, 'EUR', 'en') -> "EUR 500,000"
 *   formatCurrency(500000, 'EUR', 'de') -> "500.000 EUR"
 *   formatCurrency(500000, 'USD', 'en') -> "$500,000"
 *   formatCurrency(2000000, 'EUR', 'fr') -> "2 000 000 EUR"
 */
export function formatCurrency(
  amount: number | null | undefined,
  currency: string = 'EUR',
  locale: string = 'en'
): string {
  if (amount == null || isNaN(amount)) return '\u2014';

  // Map our 2-letter language codes to BCP 47 locale tags
  const localeMap: Record<string, string> = {
    en: 'en-US', de: 'de-DE', fr: 'fr-FR', es: 'es-ES', it: 'it-IT',
    pt: 'pt-PT', nl: 'nl-NL', pl: 'pl-PL', ro: 'ro-RO', bg: 'bg-BG',
    hr: 'hr-HR', cs: 'cs-CZ', da: 'da-DK', et: 'et-EE', fi: 'fi-FI',
    el: 'el-GR', hu: 'hu-HU', ga: 'en-IE', lv: 'lv-LV', lt: 'lt-LT',
    mt: 'en-MT', sk: 'sk-SK', sl: 'sl-SI', sv: 'sv-SE',
  };

  const bcp47 = localeMap[locale] || 'en-US';

  try {
    return new Intl.NumberFormat(bcp47, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString()} ${currency}`;
  }
}

/**
 * Format a large currency amount compactly (e.g. $1.2M, EUR 500K).
 * Uses locale-aware currency symbols for the prefix.
 */
export function formatCurrencyCompact(
  amount: number | null | undefined,
  currency: string = 'EUR',
  locale: string = 'en'
): string {
  if (amount == null || isNaN(amount)) return '\u2014';

  const localeMap: Record<string, string> = {
    en: 'en-US', de: 'de-DE', fr: 'fr-FR', es: 'es-ES', it: 'it-IT',
    pt: 'pt-PT', nl: 'nl-NL', pl: 'pl-PL', ro: 'ro-RO', bg: 'bg-BG',
    hr: 'hr-HR', cs: 'cs-CZ', da: 'da-DK', et: 'et-EE', fi: 'fi-FI',
    el: 'el-GR', hu: 'hu-HU', ga: 'en-IE', lv: 'lv-LV', lt: 'lt-LT',
    mt: 'en-MT', sk: 'sk-SK', sl: 'sl-SI', sv: 'sv-SE',
  };

  const bcp47 = localeMap[locale] || 'en-US';

  // Get the currency symbol from Intl
  let symbol: string;
  try {
    const parts = new Intl.NumberFormat(bcp47, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    symbol = parts.find(p => p.type === 'currency')?.value ?? currency;
  } catch {
    symbol = currency;
  }

  const abs = Math.abs(amount);
  let formatted: string;
  if (abs >= 1_000_000_000) {
    formatted = `${symbol}${(amount / 1_000_000_000).toFixed(1)}B`;
  } else if (abs >= 1_000_000) {
    formatted = `${symbol}${(amount / 1_000_000).toFixed(1)}M`;
  } else if (abs >= 1_000) {
    formatted = `${symbol}${(amount / 1_000).toFixed(0)}K`;
  } else if (amount === 0) {
    formatted = `${symbol}0`;
  } else {
    formatted = `${symbol}${amount.toLocaleString(bcp47)}`;
  }

  return formatted;
}

/**
 * Format a number without currency (just locale-aware grouping).
 */
export function formatNumber(
  value: number | null | undefined,
  locale: string = 'en',
  decimals: number = 0
): string {
  if (value == null || isNaN(value)) return '\u2014';

  const localeMap: Record<string, string> = {
    en: 'en-US', de: 'de-DE', fr: 'fr-FR', es: 'es-ES', it: 'it-IT',
    pt: 'pt-PT', nl: 'nl-NL', pl: 'pl-PL', ro: 'ro-RO', bg: 'bg-BG',
    hr: 'hr-HR', cs: 'cs-CZ', da: 'da-DK', et: 'et-EE', fi: 'fi-FI',
    el: 'el-GR', hu: 'hu-HU', ga: 'en-IE', lv: 'lv-LV', lt: 'lt-LT',
    mt: 'en-MT', sk: 'sk-SK', sl: 'sl-SI', sv: 'sv-SE',
  };

  const bcp47 = localeMap[locale] || 'en-US';

  return new Intl.NumberFormat(bcp47, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}
